import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }
const _hoisted_4 = {
  for: "callParticipants",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_5 = { class: "flex flex-col justify-end pb-1.5" }

import { computed, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';

import { useUiStore } from '@/stores';
import { AnalyticsService, WordService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { goBack } from '@/composables/useNavigation';

import {
    CallParticipantType,
    DefaultServiceResult,
    FormAction,
    Permission,
    WordFormState,
    WordModel,
    WordServiceCreateResult,
} from '@/types';
import { required, maxLength } from '@/utils/validators';


export default /*@__PURE__*/_defineComponent({
  __name: 'WordFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const route = useRoute();
const { t } = useI18n();

const props = __props;

const uiStore = useUiStore();

const formState = reactive<WordFormState>({
    word: null,
    callParticipant: null,
    isActive: true,
});

const rules = computed(() => ({
    word: { required, maxLength: maxLength(15) },
    callParticipant: { required },
    isActive: { required },
}));

const v$ = useVuelidate(rules, formState);

const participants = computed(() => [
    { value: CallParticipantType.All, label: t(`word.participant.${CallParticipantType.All}`) },
    { value: CallParticipantType.Agent, label: t(`word.participant.${CallParticipantType.Agent}`) },
    {
        value: CallParticipantType.Client,
        label: t(`word.participant.${CallParticipantType.Client}`),
    },
]);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchWord = async (id: number) => {
    setLoading(true);
    try {
        const { data } = await WordService.get<WordModel>(id);
        Object.assign(formState, data);
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

const handleServiceSuccess = (message: string, id: number) => {
    toast.success(message);
    v$.value.$reset();
    goBack({ name: 'ViewWordById', params: { id } });
};

const onSubmit = async () => {
    if (!(await v$.value.$validate())) return;

    setLoading(true);
    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data } = await WordService.update<DefaultServiceResult>(id, formState);
            handleServiceSuccess(data.message, id);
        } else {
            const { data } = await WordService.create<WordServiceCreateResult>(formState);
            AnalyticsService.trackingAction('NewWord');
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (err) {
        toastServiceError(err);
    } finally {
        setLoading(false);
    }
};

onMounted(() => {
    if (props.action === FormAction.EDIT) {
        fetchWord(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('word.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListWord' } }),
          (
                            (props.action == _unref(FormAction).CREATE && _ctx.$can(_unref(Permission).CREATE_WORD)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_WORD))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: _unref(v$).word.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).word.$model) = $event)),
            name: "word",
            type: "text",
            label: _ctx.$t('word.labels.word'),
            errors: _unref(v$).word.$errors,
            onBlur: _unref(v$).word.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
          _createElementVNode("div", null, [
            _createElementVNode("label", _hoisted_4, _toDisplayString(_ctx.$t('word.labels.callParticipant')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "callParticipants",
              modelValue: formState.callParticipant,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.callParticipant) = $event)),
              primitive: "",
              options: participants.value,
              errors: _unref(v$).callParticipant.$errors
            }, null, 8, ["modelValue", "options", "errors"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_UiCheckboxInput, {
              modelValue: _unref(v$).isActive.$model,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
              name: "isActive",
              label: _ctx.$t('word.labels.isActive'),
              class: "h-6"
            }, null, 8, ["modelValue", "label"])
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})